// "Download the till" on the home page (ADR 0108, step 3). It reads latest.json from the downloads
// address (a public Cloudflare R2 bucket) and offers the Mac installer. It is a plain, unauthenticated
// fetch to another origin: never the API client, which would send the owner's token there.

import type { JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

const DEFAULT_BASE = 'https://downloads.tableos.online';

// A file name is checked against this shape before it is put in a link, so a bad latest.json cannot
// point the button anywhere but at an installer in the till folder.
const INSTALLER = /^TableOS-Till-\d+\.\d+\.\d+-(arm64|x64)\.dmg$/;

const latestSchema = z.object({
  schema: z.literal(1),
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  date: z.string().datetime(),
  notes: z.string().max(2000).default(''),
  downloads: z
    .array(
      z.object({
        platform: z.literal('mac'),
        arch: z.enum(['arm64', 'x64']),
        file: z.string().regex(INSTALLER),
        bytes: z.number().int().positive(),
        sha256: z.string().regex(/^[0-9a-f]{64}$/),
      }),
    )
    .min(1),
});

export type TillLatest = z.infer<typeof latestSchema>;

/** The downloads address from the build, without a trailing slash. */
export function downloadsBase(
  raw: string | undefined = import.meta.env.VITE_DOWNLOADS_BASE_URL,
): string {
  const v = (raw ?? '').trim().replace(/\/+$/, '');
  return /^https?:\/\/[^\s/]+$/.test(v) ? v : DEFAULT_BASE;
}

export function formatMegabytes(bytes: number): string {
  return `${Math.round(bytes / 1_000_000)} MB`;
}

const CHIP: Record<'arm64' | 'x64', string> = {
  arm64: 'Mac with Apple chip (M1, M2, M3 or M4)',
  x64: 'Mac with Intel chip',
};

async function fetchLatest(base: string): Promise<TillLatest> {
  const res = await fetch(`${base}/till/latest.json`, { credentials: 'omit', cache: 'no-cache' });
  if (!res.ok) throw new Error(`latest.json answered ${res.status}`);
  return latestSchema.parse(await res.json());
}

export function TillDownloadCard({ base = downloadsBase() }: { base?: string }): JSX.Element {
  const latest = useQuery({
    queryKey: ['till-latest', base],
    queryFn: () => fetchLatest(base),
    retry: false,
    staleTime: 5 * 60_000,
  });

  return (
    <section className="card till-download" aria-labelledby="till-download-title">
      <h2 id="till-download-title">Download the till</h2>
      {latest.isLoading ? <p className="muted">Looking for the latest version…</p> : null}
      {latest.isError ? (
        <p className="muted" role="status">
          Download is not available right now.
        </p>
      ) : null}
      {latest.data ? (
        <>
          <p className="muted">
            Version <strong>{latest.data.version}</strong> ·{' '}
            {new Date(latest.data.date).toLocaleDateString(undefined, {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
            {latest.data.notes ? ` · ${latest.data.notes}` : ''}
          </p>
          <div className="row-actions">
            {latest.data.downloads.map((d) => (
              <a
                key={d.file}
                className="button"
                href={`${base}/till/${d.file}`}
                download
                rel="noopener"
              >
                Download for {CHIP[d.arch]} · {formatMegabytes(d.bytes)}
              </a>
            ))}
          </div>
          <details>
            <summary>How to open it the first time</summary>
            <ol className="muted">
              <li>Open the downloaded file and drag TableOS Till into Applications.</li>
              <li>
                The app is ad-hoc signed, not by an Apple-registered developer, so your Mac may say
                it cannot verify the developer. Click <strong>Done</strong>, then open{' '}
                <strong>System Settings, Privacy &amp; Security</strong>, scroll to the Security
                section and click <strong>Open Anyway</strong> next to TableOS Till. Confirm with
                your password, then open the app again. You only do this once. (On older versions of
                macOS, right-click TableOS Till, choose Open, then Open.)
              </li>
              <li>
                A newer version installs the same way, over the old one. The till&apos;s data is
                kept.
              </li>
            </ol>
          </details>
        </>
      ) : null}
    </section>
  );
}
